import React, { useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { Close as CloseIcon } from '@mui/icons-material';
import { Box, Button, Container, FormLabel, Typography, useTheme } from '@mui/material';
import {
  COLOR_CHATHAMS_BLUE,
  COLOR_DENIM,
  COLOR_DISABLED_BG,
  COLOR_DISABLED_TEXT,
  COLOR_HUMMINGBIRD,
  COLOR_RED,
  COLOR_REGENT_ST_BLUE,
  COLOR_WHITE,
  FONT_WEIGHT_SEMI_BOLD,
} from 'popTheme';
import SignInBg from './signin_background.svg';
import { PopInputField } from './PopInputField';
import { signInSaga } from 'redux/sagas/signInSaga';

export const SignIn = ({ handleClose }) => {
  const intl = useIntl();
  const messages = defineMessages({
    signInLabelWelcome: {
      id: 'signin.label.welcome',
      defaultMessage: 'Welcome back!',
      description: 'Welcome text for sign in section',
    },
    signInLabelSecondary: {
      id: 'signin.label.secondary',
      defaultMessage: 'You know what to do. 👇',
      description: 'Secondary text for sign in section',
    },
    signInLabelEmail: {
      id: 'signin.label.email',
      defaultMessage: 'Email Address',
      description: 'Label for the email input',
    },
    signInLabelPassword: {
      id: 'signin.label.password',
      defaultMessage: 'Password',
      description: 'Label for the password input',
    },
    signInLabelButton: {
      id: 'signin.label.button',
      defaultMessage: 'Sign In',
      description: 'Label for the submit button',
    },
    signInLabelCreateAccountLink: {
      id: 'signin.label.create.account',
      defaultMessage: 'New to the portal? Create an account.',
      description: 'Link text for create account',
    },
    signInLabelForgotPasswordLink: {
      id: 'signin.label.forgot.password',
      defaultMessage: 'Forgot Your Password?',
      description: 'Link text for forgot password',
    },
    signInValidationErrorMessage: {
      id: 'signin.validation.error.message',
      defaultMessage: 'Incorrect email or password.',
      description: 'Error text when credential check fails',
    },
    passwordExpiredErrorMessage: {
      id: 'password.expired.error.message',
      defaultMessage: 'Password expired, click {link} to reset',
      description: 'Error text when password has expired',
    },
    passwordResetLinkText: {
      id: 'password.reset.link.text',
      defaultMessage: 'here',
      description: 'Link text for password reset',
    },
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const [form, setForm] = useState({ email: '', password: '' });
  const { isSignedIn, signInError, lockedOut } = useSelector((state) => state.authorization);
  const sending = useSelector((state) => state.serverCommunication.sending);
  const isDisabled = form.email.length === 0 || form.password.length === 0 || sending;

  const onChange = (input, inputRef) => (e) => {
    if (input) {
      setForm({ ...form, [input]: '' });
      inputRef.current.focus();
    } else {
      setForm({ ...form, [e.target.id]: e.target.value });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    dispatch(signInSaga({ username: data.get('email'), password: data.get('password') }));
  };

  useEffect(() => {
    if (isSignedIn) {
      navigate('/');
    }
  });

  let emailInput = useRef(null);
  let passwordInput = useRef(null);

  const signInErrorMsg = useSelector((state) => state?.authorization?.signInErrorMsg?.password);
  const isPasswordExpired = signInErrorMsg?.includes('Password expired');

  const passwordResetLink = (
    <NavLink to="/forgotpassword" style={{ color: COLOR_WHITE }}>
      {intl.formatMessage(messages.passwordResetLinkText)}
    </NavLink>
  );

  return (
    <Container
      component="main"
      sx={{
        backgroundImage: `url(${SignInBg})`,
        bgcolor: COLOR_CHATHAMS_BLUE,
        backgroundSize: 'cover',
        width: '100%',
        height: '100%',
        maxWidth: 'fit-content',
        borderRadius: '0.75rem',
        p: '0rem',
      }}
    >
      <Box onClick={handleClose} display={{ sm: 'none' }} textAlign="right">
        <CloseIcon
          sx={{
            color: COLOR_REGENT_ST_BLUE,
            position: 'absolute',
            fontSize: '1.25rem',
            top: '1rem',
            right: '1rem',
          }}
        />
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{
          [theme.breakpoints.up('xs')]: {
            gap: '0.625rem',
            px: '3.25rem',
            py: '5.625rem',
          },
          [theme.breakpoints.up('sm')]: {
            gap: '0.9375rem',
            px: '3.75rem',
            py: '9.0625rem',
          },
        }}
      >
        <Typography
          variant="h2"
          sx={{
            color: COLOR_WHITE,
            [theme.breakpoints.up('xs')]: {
              fontSize: '1.5625rem',
            },
            [theme.breakpoints.up('sm')]: {
              fontSize: '2rem',
            },
            [theme.breakpoints.up('lg')]: {
              fontSize: '2.4375rem',
            },
          }}
        >
          {intl.formatMessage(messages.signInLabelWelcome)}
        </Typography>
        <Typography
          sx={{
            color: COLOR_WHITE,
            [theme.breakpoints.up('xs')]: {
              fontSize: '0.8125rem',
            },
            [theme.breakpoints.up('sm')]: {
              fontSize: '1rem',
            },
            [theme.breakpoints.up('lg')]: {
              fontSize: '1.25rem',
            },
          }}
        >
          {intl.formatMessage(messages.signInLabelSecondary)}
        </Typography>
        <Box
          component="form"
          data-testid="signin-form"
          onSubmit={handleSubmit}
          noValidate
          alignItems="center"
          sx={{
            [theme.breakpoints.up('xs')]: {
              minWidth: '16rem',
            },
            [theme.breakpoints.up('sm')]: {
              minWidth: '26.25rem',
            },
          }}
        >
          <Box py={{ xs: '0.625rem', sm: '0.9375rem' }}>
            <FormLabel
              required
              sx={{
                color: COLOR_WHITE,
                fontWeight: FONT_WEIGHT_SEMI_BOLD,
                '.MuiFormLabel-asterisk': {
                  color: COLOR_RED,
                },
                [theme.breakpoints.up('xs')]: {
                  px: '0.625rem',
                  py: '0.5rem',
                  fontSize: '0.625rem',
                },
                [theme.breakpoints.up('sm')]: {
                  px: '0.9375rem',
                  py: '0.625rem',
                  fontSize: '0.8125rem',
                },
              }}
            >
              {intl.formatMessage(messages.signInLabelEmail)}
            </FormLabel>
            <PopInputField
              error={signInError}
              onChange={onChange(null)}
              value={form.email}
              inputRef={emailInput}
              required
              fullWidth
              id="email"
              name="email"
              autoComplete="email"
              autoFocus
              size="small"
              data-testid="signin-email-input"
              onClickClearButton={onChange('email', emailInput)}
              iconDataTestId="signin-email-clear"
            />
          </Box>
          <Box py={{ xs: '0.625rem', sm: '0.9375rem' }}>
            <FormLabel
              required
              sx={{
                color: COLOR_WHITE,
                fontWeight: FONT_WEIGHT_SEMI_BOLD,
                '.MuiFormLabel-asterisk': {
                  color: COLOR_RED,
                },
                [theme.breakpoints.up('xs')]: {
                  px: '0.625rem',
                  py: '0.5rem',
                  fontSize: '0.625rem',
                },
                [theme.breakpoints.up('sm')]: {
                  px: '0.9375rem',
                  py: '0.625rem',
                  fontSize: '0.8125rem',
                },
              }}
              data-testid="password-label"
            >
              {intl.formatMessage(messages.signInLabelPassword)}
            </FormLabel>
            <PopInputField
              error={signInError}
              onChange={onChange(null)}
              value={form.password}
              inputRef={passwordInput}
              required
              fullWidth
              name="password"
              type="password"
              id="password"
              size="small"
              data-testid="signin-password-input"
              onClickClearButton={onChange('password', passwordInput)}
              iconDataTestId="signin-password-clear"
            />
          </Box>
          {(isPasswordExpired && (
            <Typography
              variant="subtitle1"
              align="center"
              color={COLOR_WHITE}
              py="0.625rem"
              fontSize={{ xs: '0.625rem', sm: '0.8125rem' }}
            >
              {intl.formatMessage(messages.passwordExpiredErrorMessage, {
                link: passwordResetLink,
              })}
            </Typography>
          )) ||
            (signInError && (
              <Typography
                variant="subtitle1"
                align="center"
                color={COLOR_WHITE}
                py="0.625rem"
                fontSize={{ xs: '0.625rem', sm: '0.8125rem' }}
              >
                {intl.formatMessage(messages.signInValidationErrorMessage)}
              </Typography>
            ))}
          {lockedOut && (
            <Typography
              variant="subtitle1"
              align="center"
              color={COLOR_WHITE}
              py="0.625rem"
              fontSize={{ xs: '0.625rem', sm: '0.8125rem' }}
            >
              {intl.formatMessage({
                id: 'signin.validation.error.locked-out',
                defaultMessage: 'Too many password attempts. Try again in 30 minutes.',
              })}
            </Typography>
          )}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            sx={{
              [theme.breakpoints.up('xs')]: {
                px: '1.4375rem',
                pt: '0.9375rem',
              },
              [theme.breakpoints.up('sm')]: {
                px: '6.5625rem',
                pt: '1.25rem',
              },
            }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={isDisabled}
              data-testid="signin-submit-button"
              sx={{
                bgcolor: COLOR_HUMMINGBIRD,
                color: COLOR_DENIM,
                minWidth: '13.125rem',
                fontWeight: 600,
                px: '0.625rem',
                ':hover': {
                  opacity: 0.5,
                },
                '&.Mui-disabled': {
                  background: COLOR_DISABLED_BG,
                  color: COLOR_DISABLED_TEXT,
                },
                [theme.breakpoints.up('xs')]: {
                  py: '0.5rem',
                  fontSize: '0.8125rem',
                },
                [theme.breakpoints.up('sm')]: {
                  py: '0.625rem',
                  fontSize: '1rem',
                },
              }}
            >
              {intl.formatMessage(messages.signInLabelButton)}
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            [theme.breakpoints.up('xs')]: {
              gap: '0.625rem',
              py: '0.625rem',
            },
            [theme.breakpoints.up('sm')]: {
              gap: '0.9375rem',
              pt: '2.8125rem',
            },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              [theme.breakpoints.up('xs')]: {
                fontSize: '0.8125rem',
              },
              [theme.breakpoints.up('sm')]: {
                fontSize: '1rem',
              },
            }}
          >
            <NavLink to="/register" style={{ textDecoration: 'none', color: COLOR_WHITE }}>
              {intl.formatMessage(messages.signInLabelCreateAccountLink)}
            </NavLink>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 400,
              [theme.breakpoints.up('xs')]: {
                fontSize: '0.8125rem',
              },
              [theme.breakpoints.up('sm')]: {
                fontSize: '1rem',
              },
            }}
          >
            <NavLink to="/forgotpassword" style={{ color: COLOR_WHITE }}>
              {intl.formatMessage(messages.signInLabelForgotPasswordLink)}
            </NavLink>
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};
